import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { MetaDefinition } from '../../models/meta-definition.model';
import { buildSeoUrl, isNoIndexRobotsContent } from '../../utils/canonical-url.util';

const CANONICAL_LINK_ID = 'canonical-link';

export interface HrefLangEntry {
    codeIso: string;
    url: string;
}

@Injectable({
    providedIn: 'root',
})
export class SeoService {
    constructor(
        private title: Title,
        private meta: Meta,
        @Inject(DOCUMENT) private document: Document
    ) {}

    setTitle(title: string): void {
        this.title.setTitle(title);
    }

    addMeta(metaDefinition: MetaDefinition): void {
        this.meta.addTag(metaDefinition);
    }

    setMeta(metaDefinition: MetaDefinition): void {
        this.meta.updateTag(metaDefinition);
    }

    removeMeta(selector: string): void {
        this.meta.removeTag(selector);
    }

    setCanonical(href: string): void {
        let link = this.document.getElementById(CANONICAL_LINK_ID) as HTMLLinkElement | null;

        if (!link) {
            link = this.document.createElement('link');
            link.id = CANONICAL_LINK_ID;
            link.rel = 'canonical';
            this.document.head.appendChild(link);
        }

        link.href = href;
    }

    removeCanonical(): void {
        const link = this.document.getElementById(CANONICAL_LINK_ID);
        link?.parentNode?.removeChild(link);
    }

    removeHreflangLinks(): void {
        const links = this.document.head.querySelectorAll('link[rel="alternate"]');
        links.forEach(link => link.parentNode?.removeChild(link));
    }

    setHreflangLinks(entries: HrefLangEntry[]): void {
        const baseUrl = environment.seo.canonicalBaseUrl;

        entries.forEach(urlData => {
            const link = this.document.createElement('link');
            link.rel = 'alternate';
            link.hreflang = urlData.codeIso;
            link.href = buildSeoUrl(urlData.url, baseUrl);
            this.document.head.appendChild(link);
        });
    }

    hasNoIndexMeta(): boolean {
        const robotsTag = this.meta.getTag('name="robots"');
        const content = robotsTag?.content ?? '';

        return isNoIndexRobotsContent(content);
    }
}
